import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { AvaliacaoAgenciaCard } from "@/components/agencia/AvaliacaoAgenciaCard";
import { AvaliacaoAgenciaFilters } from "@/components/agencia/AvaliacaoAgenciaFilters";
import { normalizeString } from "@/utils/searchUtils";
import type {
  AvaliacaoAgenciaListItem,
  AvaliacaoUsuarioAgencia,
  AvaliadorInfo,
  FiltroAvaliacaoAgencia,
  JobEvaluationListItem,
} from "@/types/avaliacao";

export default function AvaliacoesAgencia() {
  const { user } = useAuth();
  const { agenciaId } = useUserPermissions();
  const [filtro, setFiltro] = useState<FiltroAvaliacaoAgencia>("todas");
  const [buscaNome, setBuscaNome] = useState("");
  const [buscaNucleo, setBuscaNucleo] = useState("");

  // Query: job_evaluations do tipo agency para a agência do usuário
  const { data: avaliacoes = [], isLoading: isLoadingAvaliacoes } = useQuery({
    queryKey: ["avaliacoes-agencia", agenciaId],
    queryFn: async () => {
      if (!agenciaId) return [];

      const { data, error } = await supabase
        .from("job_evaluations")
        .select(`
          id,
          controle_projeto_id,
          evaluator_type,
          status,
          rating,
          media_geral,
          rated_at,
          created_at,
          feedback_text,
          feedback_data,
          controle_projetos!inner(
            freelancer_id,
            agencia_id,
            data_inicio,
            data_conclusao,
            nucleo,
            numero_renovacoes,
            freelancers!inner(nome, email),
            agencias!inner(nome, email)
          )
        `)
        .eq("evaluator_type", "agency")
        .eq("origin", "external")
        .eq("controle_projetos.agencia_id", agenciaId)
        .is("deleted_at", null)
        .is("controle_projetos.deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data || []).map((item: any) => ({
        id: item.id,
        controle_projeto_id: item.controle_projeto_id,
        freelancer_id: item.controle_projetos?.freelancer_id,
        agencia_id: item.controle_projetos?.agencia_id,
        evaluator_type: item.evaluator_type,
        status: item.status,
        rating: item.rating,
        media_geral: item.media_geral,
        rated_at: item.rated_at,
        created_at: item.created_at,
        feedback_text: item.feedback_text,
        feedback_data: item.feedback_data,
        freelancer_name: item.controle_projetos?.freelancers?.nome,
        agencia_name: item.controle_projetos?.agencias?.nome,
        freelancer_email: item.controle_projetos?.freelancers?.email,
        agencia_email: item.controle_projetos?.agencias?.email,
        project_start_date: item.controle_projetos?.data_inicio,
        project_end_date: item.controle_projetos?.data_conclusao,
        project_nucleo: item.controle_projetos?.nucleo,
        numero_renovacoes: item.controle_projetos?.numero_renovacoes ?? 0,
      })) as JobEvaluationListItem[];
    },
    enabled: !!agenciaId,
    staleTime: 2 * 60 * 1000,
  });

  // Query: avaliações do usuário atual
  const { data: minhasAvaliacoes = [], isLoading: isLoadingMinhas } = useQuery({
    queryKey: ["minhas-avaliacoes-agencia", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("avaliacoes_usuario_agencia")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      return data as AvaliacaoUsuarioAgencia[];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });

  // Query: todas as avaliações da agência (para saber quem avaliou cada job_evaluation)
  const { data: todasAvaliacoesAgencia = [] } = useQuery({
    queryKey: ["todas-avaliacoes-agencia", agenciaId, avaliacoes.map((a) => a.id).join(",")],
    queryFn: async () => {
      if (!agenciaId || avaliacoes.length === 0) return [];

      const jobEvalIds = avaliacoes.map((a) => a.id);

      // Buscar todas as avaliacoes_usuario_agencia (sem join — não há FK para profiles)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: avalData, error: avalError } = await (supabase as any)
        .from("avaliacoes_usuario_agencia")
        .select("job_evaluation_id, user_id")
        .in("job_evaluation_id", jobEvalIds);

      if (avalError) throw avalError;
      if (!avalData || avalData.length === 0) return [];

      // Buscar nomes dos usuários que avaliaram
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userIds = [...new Set((avalData as any[]).map((a: any) => a.user_id))];
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      const profilesMap = new Map<string, string>();
      for (const p of profilesData || []) {
        profilesMap.set(p.id, p.full_name ?? "Usuário");
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (avalData as any[]).map((item: any) => ({
        job_evaluation_id: item.job_evaluation_id,
        user_id: item.user_id,
        full_name: profilesMap.get(item.user_id) ?? "Usuário",
      }));
    },
    enabled: !!agenciaId && avaliacoes.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  // Combinar avaliações com as do usuário e avaliadores
  const avaliacoesComMinha: AvaliacaoAgenciaListItem[] = useMemo(() => {
    const minhasMap = new Map<string, AvaliacaoUsuarioAgencia>();
    for (const a of minhasAvaliacoes) {
      minhasMap.set(a.job_evaluation_id, a);
    }

    // Agrupar avaliadores por job_evaluation_id
    const avaliadoresMap = new Map<string, AvaliadorInfo[]>();
    for (const a of todasAvaliacoesAgencia) {
      const list = avaliadoresMap.get(a.job_evaluation_id) || [];
      list.push({ user_id: a.user_id, full_name: a.full_name });
      avaliadoresMap.set(a.job_evaluation_id, list);
    }

    const combined = avaliacoes.map((av) => ({
      ...av,
      minha_avaliacao: minhasMap.get(av.id) || null,
      avaliadores: avaliadoresMap.get(av.id) || [],
    }));

    // Ordenar: avaliados primeiro, depois por created_at desc
    combined.sort((a, b) => {
      const aAvaliado = a.minha_avaliacao ? 1 : 0;
      const bAvaliado = b.minha_avaliacao ? 1 : 0;
      if (aAvaliado !== bAvaliado) return bAvaliado - aAvaliado;
      // Dentro do mesmo grupo, manter created_at desc
      return (b.created_at ?? "").localeCompare(a.created_at ?? "");
    });

    return combined;
  }, [avaliacoes, minhasAvaliacoes, todasAvaliacoesAgencia]);

  // Filtrar por status, nome e núcleo
  const avaliacoesFiltradas = useMemo(() => {
    let resultado = avaliacoesComMinha;

    // Filtro por status
    if (filtro === "avaliadas") {
      resultado = resultado.filter((a) => !!a.minha_avaliacao);
    } else if (filtro === "nao_avaliadas") {
      resultado = resultado.filter((a) => !a.minha_avaliacao);
    }

    // Filtro por nome
    if (buscaNome.trim()) {
      const normalizedBusca = normalizeString(buscaNome);
      resultado = resultado.filter((a) =>
        a.freelancer_name ? normalizeString(a.freelancer_name).includes(normalizedBusca) : false
      );
    }

    // Filtro por núcleo
    if (buscaNucleo.trim()) {
      const normalizedBusca = normalizeString(buscaNucleo);
      resultado = resultado.filter((a) =>
        a.project_nucleo ? normalizeString(a.project_nucleo).includes(normalizedBusca) : false
      );
    }

    return resultado;
  }, [avaliacoesComMinha, filtro, buscaNome, buscaNucleo]);

  const isLoading = isLoadingAvaliacoes || isLoadingMinhas;

  const totalAvaliadas = avaliacoesComMinha.filter((a) => !!a.minha_avaliacao).length;
  const totalNaoAvaliadas = avaliacoesComMinha.filter((a) => !a.minha_avaliacao).length;

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        title="Avaliações"
        subtitle={`${avaliacoesComMinha.length} avaliações disponíveis (${totalAvaliadas} avaliadas por você)`}
      />

      <AvaliacaoAgenciaFilters
        filtro={filtro}
        onChange={setFiltro}
        buscaNome={buscaNome}
        onBuscaNomeChange={setBuscaNome}
        buscaNucleo={buscaNucleo}
        onBuscaNucleoChange={setBuscaNucleo}
        totalNaoAvaliadas={totalNaoAvaliadas}
      />

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="w-full h-[80px] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {avaliacoesFiltradas.length > 0 ? (
            avaliacoesFiltradas.map((avaliacao) => (
              <AvaliacaoAgenciaCard
                key={avaliacao.id}
                avaliacao={avaliacao}
              />
            ))
          ) : (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">
                {filtro === "todas" && !buscaNome && !buscaNucleo
                  ? "Nenhuma avaliação encontrada para sua agência."
                  : filtro === "avaliadas" && !buscaNome && !buscaNucleo
                  ? "Você ainda não avaliou nenhum freelancer."
                  : filtro === "nao_avaliadas" && !buscaNome && !buscaNucleo
                  ? "Todas as avaliações já foram preenchidas."
                  : "Nenhuma avaliação encontrada com os filtros selecionados."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
